import { Close24 } from '@carbon/icons-react';
import { moderate02 } from '@carbon/motion';
import { Button } from 'carbon-components-react';
import classNames from 'classnames';
import { CSSProperties, forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';

import classes from './Popover.module.scss';
import removeUnit from '../carbon/removeUnit';

type PopoverPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

interface Props {
  header: string;
  isOpen: boolean;
  offset?: number;
  onClose: () => void;
  position: PopoverPosition;
  text: string;
}

const getPopoverPositionClassNames = (position: PopoverPosition) => {
  switch (position) {
    case 'top-left':
      return [classes.popover_x_left, classes.popover_y_top];
    case 'top-right':
      return [classes.popover_x_right, classes.popover_y_top];
    case 'bottom-left':
      return [classes.popover_x_left, classes.popover_y_bottom];
    case 'bottom-right':
      return [classes.popover_x_right, classes.popover_y_bottom];
    default:
      throw new Error('Unknown position');
  }
};

// TODO #1 ! Disallow focusing on openButton, when popup is open.
// TODO #1 Better animation?
// - Hide button, then expand popup, then show popup text.
const Popover = forwardRef<HTMLDivElement, Props>(
  ({ header, isOpen, offset = 0, onClose, position, text }, forwardedRef) => {
    const ref = useRef(null);

    // TODO #1 ? Is this a correct way of accessing forwarded ref?
    useImperativeHandle(forwardedRef, () => ref.current);

    // Listen to document events and close the popover on selected ones.
    useEffect(() => {
      // Don't bind the close listeners when popover isn't open.
      if (!isOpen) {
        return undefined;
      }
      // Close the popover when the user clicks outside.
      const handleClick = event => {
        const element = ref.current;
        if (element != null && !element.contains(event.target)) {
          onClose();
        }
      };
      // Close the popover when the user presses escape key.
      const handleKeyDown = event => {
        if (event.key === 'Escape') {
          onClose();
        }
      };
      // Delay listening to the events, otherwise it will react to the event that triggered
      // the popover opening.
      const timeout = setTimeout(() => {
        document.addEventListener('click', handleClick);
        document.addEventListener('keydown', handleKeyDown);
      });
      return () => {
        clearTimeout(timeout);
        document.removeEventListener('click', handleClick);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [isOpen, onClose]);

    return (
      <CSSTransition
        classNames={{
          enter: classes.popover_enter,
          enterActive: classes.popover_enterActive,
          exit: classes.popover_exit,
          exitActive: classes.popover_exitActive,
        }}
        in={isOpen}
        mount
        timeout={removeUnit(moderate02, 'ms')}
        unmountOnExit
      >
        <div
          className={classNames(classes.popover, ...getPopoverPositionClassNames(position))}
          ref={ref}
          style={{ '--duration': moderate02, '--offset': `${offset}px` } as CSSProperties}
        >
          <div className={classes.container}>
            <h2 className={classes.header}>{header}</h2>
            <p className={classes.text}>{text}</p>
          </div>
          <Button
            type="button"
            className={classes.closeButton}
            hasIconOnly
            iconDescription="Close"
            kind="secondary"
            onClick={onClose}
            renderIcon={Close24}
            size="md"
          />
        </div>
      </CSSTransition>
    );
  },
);

export default Popover;
